import "@jcodigital/jcore-utils";

/*
 * Mobile vh fix.
 */
let vh = window.innerHeight * 0.01;
document.documentElement.style.setProperty("--vh", `${vh}px`);

// We listen to the resize event
window.addEventListener("resize", () => {
  // We execute the same script as before
  let vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty("--vh", `${vh}px`);
});

/*
 * Scroll to top functionality
 */
let scrollButton: HTMLElement | null;

function footerInit() {
  scrollButton = document.getElementById("scroll-to-top");
  if (scrollButton) {
    window.addEventListener("scroll", scrollFunction);
    scrollButton.addEventListener("click", () => {
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0;
    });
  }
}

function scrollFunction() {
  if (
    document.body.scrollTop > 600 ||
    document.documentElement.scrollTop > 600
  ) {
    scrollButton.style.opacity = 1;
  } else {
    scrollButton.style.opacity = 0;
  }
}
/* End Scroll to top */

/**
 * Masonry Grid
 */
window.addEventListener("DOMContentLoaded", () => {
  footerInit();
  resizeMasonryGrid();
  const config = { attributes: false, childList: true, subtree: false };
  document.querySelectorAll(".masonry-grid").forEach((grid) => {
    const observer = new MutationObserver(resizeMasonryGrid);
    observer.observe(grid, config);
  });
});
window.addEventListener("resize", () => {
  resizeMasonryGrid();
});

function resizeMasonryGrid() {
  document.querySelectorAll(".masonry-grid").forEach((grid) => {
    const rowHeight = parseInt(
      window.getComputedStyle(grid).getPropertyValue("grid-auto-rows"),
    );
    const rowGap = parseInt(
      window.getComputedStyle(grid).getPropertyValue("grid-row-gap"),
    );
    for (const item of grid.children) {
      let height = rowGap;
      for (const part of item.children) {
        const style = getComputedStyle(part);
        height += parseInt(style.marginTop);
        height += part.offsetHeight;
        height += parseInt(style.marginBottom);
      }
      const rowSpan = Math.ceil(height / (rowHeight + rowGap));
      item.style.gridRowEnd = "span " + rowSpan;
    }
  });
}
/* End Masonry Grid */

/**
 * Header navigation (views/lohko/navigation, partials/_navigation.scss).
 *
 * Dropdowns - every `[data-sq-dropdown]` item, in the primary menu and the
 * utility bar - open on hover on desktop and on a click of their button
 * everywhere, with `aria-expanded` kept in step. Opening one closes the
 * others. On desktop a click outside or Escape closes them; a card that
 * would run off the viewport is nudged back in.
 *
 * Below the breakpoint the hamburger opens the primary menu as a full
 * screen panel. While it is open the page behind is locked (`html.nav-open`)
 * and the panel is told where the bar ends (`--nav-panel-top`), so the list
 * starts under it. Following a link, Escape and growing past the breakpoint
 * all close it.
 */
// $nav-breakpoint in partials/_navigation.scss.
const NAV_BREAKPOINT = 1180;
const DROPDOWN_GUTTER = 20;
const HOVER_CLOSE_DELAY = 160;

function navInit() {
  const nav = document.getElementById("jcore-nav");
  if (!nav) {
    return;
  }

  const desktop = window.matchMedia(`(min-width: ${NAV_BREAKPOINT}px)`);
  const items = Array.from(
    nav.querySelectorAll<HTMLElement>("[data-sq-dropdown]"),
  );
  const toggleOf = (item: HTMLElement) =>
    item.querySelector<HTMLElement>(":scope > [aria-controls]");
  const panelOf = (item: HTMLElement) =>
    item.querySelector<HTMLElement>(":scope > .sq-dd");

  const place = (item: HTMLElement) => {
    const panel = panelOf(item);
    if (!panel) {
      return;
    }
    panel.style.setProperty("--sq-dd-shift", "0px");
    if (!desktop.matches) {
      return;
    }
    const box = panel.getBoundingClientRect();
    let shift = 0;
    if (box.right > window.innerWidth - DROPDOWN_GUTTER) {
      shift = window.innerWidth - DROPDOWN_GUTTER - box.right;
    }
    // Never push the left edge off screen to save the right one.
    if (box.left + shift < DROPDOWN_GUTTER) {
      shift = DROPDOWN_GUTTER - box.left;
    }
    panel.style.setProperty("--sq-dd-shift", `${Math.round(shift)}px`);
  };

  const setOpen = (item: HTMLElement, open: boolean) => {
    item.classList.toggle("is-open", open);
    toggleOf(item)?.setAttribute("aria-expanded", String(open));
    if (open) {
      place(item);
    }
  };

  const closeAll = (except?: HTMLElement) =>
    items.forEach((item) => item !== except && setOpen(item, false));

  let hoverTimer = 0;

  items.forEach((item) => {
    const toggle = toggleOf(item);
    if (!toggle) {
      return;
    }

    toggle.addEventListener("click", () => {
      const open = !item.classList.contains("is-open");
      closeAll(item);
      setOpen(item, open);
    });

    item.addEventListener("mouseenter", () => {
      if (!desktop.matches) {
        return;
      }
      window.clearTimeout(hoverTimer);
      closeAll(item);
      setOpen(item, true);
    });

    item.addEventListener("mouseleave", () => {
      if (!desktop.matches) {
        return;
      }
      hoverTimer = window.setTimeout(
        () => setOpen(item, false),
        HOVER_CLOSE_DELAY,
      );
    });

    // Tabbing out of an open dropdown closes it.
    item.addEventListener("focusout", (event) => {
      const next = event.relatedTarget as Node | null;
      if (desktop.matches && next && !item.contains(next)) {
        setOpen(item, false);
      }
    });
  });

  document.addEventListener("click", (event) => {
    if (
      desktop.matches &&
      !(event.target as HTMLElement).closest("[data-sq-dropdown]")
    ) {
      closeAll();
    }
  });

  window.addEventListener("resize", () =>
    items.forEach((item) => item.classList.contains("is-open") && place(item)),
  );

  /* The full screen panel. */
  const hamburger = nav.querySelector<HTMLElement>(".hamburger");
  const panel = nav.querySelector<HTMLElement>(".nav-panel");
  const bar = nav.querySelector<HTMLElement>(".sq-bar");

  const isMenuOpen = () => nav.classList.contains("is-menu-open");

  const setMenu = (open: boolean) => {
    if (!hamburger || !panel || !bar) {
      return;
    }
    if (open) {
      const top = Math.max(0, bar.getBoundingClientRect().bottom);
      panel.style.setProperty("--nav-panel-top", `${Math.round(top)}px`);
      panel.scrollTop = 0;
    } else {
      // Collapse the accordions once the panel has faded out.
      window.setTimeout(() => !isMenuOpen() && closeAll(), 300);
    }
    nav.classList.toggle("is-menu-open", open);
    document.documentElement.classList.toggle("nav-open", open);
    hamburger.setAttribute("aria-expanded", String(open));
    const label = hamburger.dataset[open ? "labelClose" : "labelOpen"];
    if (label) {
      hamburger.setAttribute("aria-label", label);
    }
  };

  hamburger?.addEventListener("click", () => setMenu(!isMenuOpen()));

  panel?.addEventListener("click", (event) => {
    // An anchor link on the same page would otherwise leave the panel
    // covering the section it just jumped to.
    if ((event.target as HTMLElement).closest("a[href]") && isMenuOpen()) {
      setMenu(false);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") {
      return;
    }
    if (isMenuOpen()) {
      setMenu(false);
      hamburger?.focus();
      return;
    }
    const open = items.find((item) => item.classList.contains("is-open"));
    if (open) {
      closeAll();
      toggleOf(open)?.focus();
    }
  });

  desktop.addEventListener("change", () => {
    closeAll();
    if (desktop.matches && isMenuOpen()) {
      setMenu(false);
    }
  });
}

/**
 * Scroll reveal: each top-level section of the page content fades and rises
 * in the first time it scrolls into view (styles in partials/_reveal.scss).
 *
 * The first block is left alone - it is the hero, already on screen - and so
 * are sticky bars, which a moving parent would unstick. Sections already in
 * view when the script runs are shown straight away rather than animated.
 */
function revealInit() {
  if (
    !("IntersectionObserver" in window) ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    return;
  }

  const sections = Array.from(
    document.querySelectorAll<HTMLElement>(
      ".wp-block-post-content > :not(:first-child)",
    ),
  ).filter(
    (el) =>
      !el.matches(".wp-block-lohko-section-nav, .wp-block-spacer") &&
      getComputedStyle(el).position !== "sticky",
  );
  if (!sections.length) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.08, rootMargin: "0px 0px -60px 0px" },
  );

  sections.forEach((section) => {
    const box = section.getBoundingClientRect();
    if (box.top < window.innerHeight && box.bottom > 0) {
      return;
    }
    section.classList.add("sq-reveal");
    observer.observe(section);
  });
}

function init() {
  navInit();
  revealInit();
}

if (document.readyState === "loading") {
  window.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
